import React from 'react';
import { Trophy, Building2, UserRound, Users } from 'lucide-react';
import { formatMoney } from './analyticsHelpers';

const cardStyle = {
  background: '#FFFFFF',
  borderRadius: 18,
  padding: 14,
  border: '1px solid rgba(112, 63, 246, 0.08)',
  boxShadow: 'var(--premium-shadow-nested)',
  flex: 1,
  minWidth: 240,
};

const Title = ({ icon, children, days }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
    {icon}
    <h3 style={{ fontFamily: "'Inria Serif', serif", fontSize: 17, fontWeight: 700, margin: 0, color: '#000', flex: 1 }}>{children}</h3>
    <span style={{ fontSize: 10, color: '#aaa', textTransform: 'uppercase', letterSpacing: 1 }}>Last {days}d</span>
  </div>
);

const Row = ({ rank, primary, secondary, right, onClick, testId }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    style={{
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      gap: 10,
      padding: '8px 6px',
      background: 'transparent',
      border: 'none',
      borderTop: '1px solid #F2F2F2',
      cursor: onClick ? 'pointer' : 'default',
      textAlign: 'left',
    }}
  >
    <span style={{ width: 22, height: 22, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', borderRadius: 999, background: '#F6F4FB', color: 'var(--accent-purple-strong)', fontSize: 11, fontWeight: 700 }}>{rank}</span>
    <div style={{ flex: 1, minWidth: 0 }}>
      <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: '#111', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{primary}</p>
      <p style={{ margin: '2px 0 0', fontSize: 11, color: '#777' }}>{secondary}</p>
    </div>
    <span style={{ fontSize: 12, fontWeight: 700, color: '#111', whiteSpace: 'nowrap' }}>{right}</span>
  </button>
);

const EmptyRow = ({ text }) => (
  <p style={{ margin: '12px 0', textAlign: 'center', fontSize: 12, color: '#aaa' }}>{text}</p>
);

export const TopPerformers = ({ data, loading, onClickVenue, onClickHost, onClickGuest }) => {
  const days = data?.days || 30;
  const venues = data?.venues || [];
  const hosts = data?.hosts || [];
  const guests = data?.guests || [];

  return (
    <div data-testid="top-performers-section" style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
      <div style={cardStyle} data-testid="top-venues-card">
        <Title icon={<Building2 size={16} color="var(--accent-purple-strong)" />} days={days}>
          <Trophy size={14} style={{ marginRight: 4, verticalAlign: 'middle' }} color="#E5A800" /> Top venues
        </Title>
        {loading && venues.length === 0 ? (
          <EmptyRow text="Loading…" />
        ) : venues.length === 0 ? (
          <EmptyRow text="No paid bookings yet in this period." />
        ) : (
          venues.map((venue, idx) => (
            <Row
              key={venue.id || idx}
              rank={idx + 1}
              primary={venue.name}
              secondary={`${venue.bookings} booking${venue.bookings === 1 ? '' : 's'}`}
              right={formatMoney(venue.revenue)}
              onClick={onClickVenue ? () => onClickVenue(venue) : undefined}
              testId={`top-venue-row-${idx}`}
            />
          ))
        )}
      </div>

      <div style={cardStyle} data-testid="top-hosts-card">
        <Title icon={<UserRound size={16} color="#00B341" />} days={days}>
          <Trophy size={14} style={{ marginRight: 4, verticalAlign: 'middle' }} color="#E5A800" /> Top hosts
        </Title>
        {loading && hosts.length === 0 ? (
          <EmptyRow text="Loading…" />
        ) : hosts.length === 0 ? (
          <EmptyRow text="No host activity in this period." />
        ) : (
          hosts.map((host, idx) => (
            <Row
              key={host.id || idx}
              rank={idx + 1}
              primary={host.name}
              secondary={`${host.bookings} booking${host.bookings === 1 ? '' : 's'} · ${formatMoney(host.earnings)} earned`}
              right={`${host.bookings}`}
              onClick={onClickHost ? () => onClickHost(host) : undefined}
              testId={`top-host-row-${idx}`}
            />
          ))
        )}
      </div>

      <div style={cardStyle} data-testid="top-guests-card">
        <Title icon={<Users size={16} color="#E52445" />} days={days}>
          <Trophy size={14} style={{ marginRight: 4, verticalAlign: 'middle' }} color="#E5A800" /> Top guests
        </Title>
        {loading && guests.length === 0 ? (
          <EmptyRow text="Loading…" />
        ) : guests.length === 0 ? (
          <EmptyRow text="No guest spend in this period." />
        ) : (
          guests.map((guest, idx) => (
            <Row
              key={guest.id || idx}
              rank={idx + 1}
              primary={guest.name}
              secondary={`${guest.bookings} booking${guest.bookings === 1 ? '' : 's'}`}
              right={formatMoney(guest.spend)}
              onClick={onClickGuest ? () => onClickGuest(guest) : undefined}
              testId={`top-guest-row-${idx}`}
            />
          ))
        )}
      </div>
    </div>
  );
};
